import type { ProblemSeverity } from '../engine/projection';

/**
 * Inline severity dot for year rows in tables / chart annotations.
 *
 * Slice 6 design intent: when the engine surfaces an "interesting" year
 * (cap hit, RMD, overstretched budget, bucket exhausted), the row gets a
 * small, calm marker — never a red alert. Tone is observational, not alarming.
 *
 * Color discipline:
 *   - `note`     → soft slate (neutral observation)
 *   - `warning`  → muted amber (worth knowing)
 *   - `critical` → warm orange (the model is at the edge of usefulness)
 *
 * Tailwind's `neg-*` (red) tokens are deliberately avoided here — these are
 * dignified observations about the projection, not crashes.
 */

interface Props {
  severity: ProblemSeverity;
  /** Hover/long-press tooltip text. Kept intentionally short — for richer
   *  context use the matching {@link ProblemPanel}. */
  tooltip?: string;
  /** Optional override label inside the chip; otherwise we render an icon glyph. */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const SEVERITY_CLS: Record<ProblemSeverity, string> = {
  note:
    'border-ink-200 bg-ink-50 text-ink-600 dark:border-ink-700 dark:bg-ink-800/60 dark:text-ink-300',
  warning:
    'border-amber-500/40 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-700/10 dark:text-amber-400',
  critical:
    'border-orange-500/40 bg-orange-50 text-orange-700 dark:border-orange-500/40 dark:bg-orange-700/10 dark:text-orange-400',
};

const SEVERITY_GLYPH: Record<ProblemSeverity, string> = {
  note: 'i',
  warning: '!',
  critical: '!!',
};

const SEVERITY_ARIA: Record<ProblemSeverity, string> = {
  note: 'Note',
  warning: 'Heads up',
  critical: 'Worth knowing',
};

const SIZE_CLS: Record<'sm' | 'md', string> = {
  sm: 'h-4 min-w-[1rem] px-1 text-[9px]',
  md: 'h-5 min-w-[1.25rem] px-1.5 text-[10px]',
};

export function ProblemBadge({
  severity,
  tooltip,
  label,
  size = 'sm',
  className = '',
}: Props) {
  const text = label ?? SEVERITY_GLYPH[severity];
  const aria = `${SEVERITY_ARIA[severity]}${tooltip ? `: ${tooltip}` : ''}`;
  return (
    <span
      title={tooltip}
      aria-label={aria}
      className={`inline-flex shrink-0 items-center justify-center rounded-full border font-bold leading-none ${SIZE_CLS[size]} ${SEVERITY_CLS[severity]} ${className}`}
    >
      {text}
    </span>
  );
}
